import { useState, useEffect, useMemo, useCallback } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import {
  getPerformanceReport,
  type PerformanceReportResponse,
  type PerformanceReportRange,
  type MonthlyReturn,
} from "../../services/portfolioService";

interface PerformanceReportProps {
  accountId?: string;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const TIME_RANGES: { value: PerformanceReportRange; label: string }[] = [
  { value: "YTD", label: "YTD" },
  { value: "1M", label: "1M" },
  { value: "2M", label: "2M" },
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
  { value: "2Y", label: "2Y" },
  { value: "3Y", label: "3Y" },
  { value: "5Y", label: "5Y" },
  { value: "ALL", label: "All" },
];

const SPY_COLOR = "#f472b6";

function usePrimaryColor(): string {
  const [color, setColor] = useState("#5a6df2");
  useEffect(() => {
    const val = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-primary-500")
      .trim();
    if (val) setColor(val);
  }, []);
  return color;
}

function formatPct(value: string | number | undefined): string {
  if (value === undefined || value === "") return "--";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "--";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
}

function pctColorClass(value: string | number | undefined): string {
  if (value === undefined || value === "") return "text-surface-600";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "text-surface-600";
  if (num > 0) return "text-emerald-400";
  if (num < 0) return "text-red-400";
  return "text-surface-500";
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDateFull(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function PerformanceReport({ accountId }: PerformanceReportProps) {
  const [data, setData] = useState<PerformanceReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<PerformanceReportRange>("ALL");
  const primaryColor = usePrimaryColor();

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const result = await getPerformanceReport(accountId, range);
      setData(result);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load performance report",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accountId, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build bar chart data from monthly returns
  const barChartData = useMemo(() => {
    if (!data?.monthly_returns) return [];
    return data.monthly_returns.map((m) => ({
      label: `${MONTH_NAMES[m.month - 1]} '${String(m.year).slice(2)}`,
      account: parseFloat(m.account_return_pct),
      spy: parseFloat(m.spy_return_pct),
    }));
  }, [data]);

  // Symmetric Y-axis domain so both account and SPY bars are visible
  const barDomain = useMemo(() => {
    if (barChartData.length === 0) return [-5, 5];
    const allVals = barChartData.flatMap((d) => [d.account, d.spy]);
    const absMax = Math.max(
      Math.abs(Math.min(...allVals)),
      Math.abs(Math.max(...allVals)),
      2,
    );
    const padded = Math.ceil(absMax * 1.15);
    return [-padded, padded] as [number, number];
  }, [barChartData]);

  // Build equity curve data
  const equityCurveData = useMemo(() => {
    if (!data?.equity_curve) return [];
    return data.equity_curve.map((p) => ({
      date: p.date,
      account: parseFloat(p.account_value),
      spy: parseFloat(p.spy_normalized),
    }));
  }, [data]);

  // Build the year×month grid for the table
  const tableData = useMemo(() => {
    if (!data?.monthly_returns || data.monthly_returns.length === 0) return [];

    // Group by year
    const byYear: Record<number, MonthlyReturn[]> = {};
    for (const m of data.monthly_returns) {
      if (!byYear[m.year]) byYear[m.year] = [];
      byYear[m.year].push(m);
    }

    // Sort years descending (most recent first)
    const years = Object.keys(byYear)
      .map(Number)
      .sort((a, b) => b - a);

    return years.map((year) => {
      const months = byYear[year];
      // Index by month number for fast lookup
      const monthMap: Record<number, MonthlyReturn> = {};
      for (const m of months) {
        monthMap[m.month] = m;
      }

      // Calculate yearly total by compounding
      const yearlyReturn = data.yearly_returns.find((y) => y.year === year);

      return { year, monthMap, yearlyReturn };
    });
  }, [data]);

  // Custom tooltip for equity curve
  const equityTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      const { active, payload, label } = props;
      if (!active || !payload || payload.length === 0) return null;
      return (
        <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg">
          <p className="text-surface-300 text-sm mb-1">
            {formatDateFull(label)}
          </p>
          {payload.map(
            (entry: { dataKey: string; color: string; value: number }) => (
              <div key={entry.dataKey} className="flex items-center gap-2 mb-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-surface-300 text-sm">
                  {entry.dataKey === "account" ? "Account" : "SPY"}:
                </span>
                <span className="text-surface-200 text-sm font-medium">
                  ${entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            ),
          )}
        </div>
      );
    },
    [],
  );

  // Custom tooltip for bar chart
  const barTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      const { active, payload, label } = props;
      if (!active || !payload || payload.length === 0) return null;
      return (
        <div className="bg-surface-800 border border-surface-600 rounded-lg p-3 shadow-lg">
          <p className="text-surface-300 text-sm mb-1">{label}</p>
          {payload.map(
            (entry: { dataKey: string; color: string; value: number }) => (
              <div key={entry.dataKey} className="flex items-center gap-2 mb-1">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-surface-300 text-sm">
                  {entry.dataKey === "account" ? "Account" : "SPY"}:
                </span>
                <span
                  className={`text-sm font-medium ${entry.value >= 0 ? "text-emerald-400" : "text-red-400"}`}
                >
                  {formatPct(entry.value)}
                </span>
              </div>
            ),
          )}
        </div>
      );
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="glass-card p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-red-400">{error}</p>
        <button
          onClick={fetchData}
          className="mt-3 text-sm text-primary-400 hover:text-primary-300"
        >
          Try again
        </button>
      </div>
    );
  }

  if (
    !data ||
    (data.monthly_returns.length === 0 && data.equity_curve.length === 0)
  ) {
    return (
      <div className="glass-card p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-800 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-surface-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-surface-200 mb-2">
          No Performance Data
        </h3>
        <p className="text-surface-400 text-sm">
          Performance data will appear after your strategies execute and daily
          snapshots are captured.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Delay Notice + Time Range Selector */}
      <div className="glass-card p-3 sm:p-4">
        <p className="text-surface-500 text-xs mb-2">
          1 day delayed — snapshots captured daily at 4:30 PM EST after market
          close
        </p>
        <div className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar pb-1">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.value}
              onClick={() => setRange(tr.value)}
              className={`time-range-btn text-xs sm:text-sm whitespace-nowrap ${
                range === tr.value
                  ? "time-range-btn-active"
                  : "time-range-btn-inactive"
              }`}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      {/* Equity Curve */}
      {equityCurveData.length > 1 && (
        <div className="glass-card p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold text-surface-100 mb-3">
            Equity Curve
          </h3>
          <div className="h-40 sm:h-48 lg:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={equityCurveData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#27272a"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke="#52525b"
                  fontSize={11}
                  tickFormatter={formatDate}
                  interval="preserveStartEnd"
                  axisLine={{ stroke: "#3f3f46" }}
                  tickLine={{ stroke: "#3f3f46" }}
                />
                <YAxis
                  orientation="right"
                  stroke="#71717a"
                  fontSize={11}
                  tickFormatter={(v) =>
                    `$${(v / 1000).toFixed(0)}k`
                  }
                  domain={["auto", "auto"]}
                  axisLine={false}
                  tickLine={false}
                  width={55}
                />
                <Tooltip content={equityTooltip} />
                <Line
                  type="monotone"
                  dataKey="account"
                  stroke={primaryColor}
                  strokeWidth={2}
                  dot={false}
                  name="Account"
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="spy"
                  stroke={SPY_COLOR}
                  strokeWidth={2}
                  dot={false}
                  name="SPY"
                  strokeDasharray="5 5"
                  connectNulls
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  formatter={(value: string) => (
                    <span className="text-surface-300 text-xs">{value}</span>
                  )}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Monthly Returns Bar Chart */}
      {barChartData.length > 0 && (
        <div className="glass-card p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold text-surface-100 mb-3">
            Monthly Returns
          </h3>
          <div className="h-36 sm:h-44 lg:h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData} barGap={1} barCategoryGap="20%">
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#27272a"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  stroke="#52525b"
                  fontSize={10}
                  interval={barChartData.length > 12 ? Math.floor(barChartData.length / 12) : 0}
                  axisLine={{ stroke: "#3f3f46" }}
                  tickLine={{ stroke: "#3f3f46" }}
                />
                <YAxis
                  orientation="right"
                  stroke="#71717a"
                  fontSize={11}
                  tickFormatter={(v) =>
                    `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`
                  }
                  domain={barDomain}
                  axisLine={false}
                  tickLine={false}
                  width={45}
                />
                <ReferenceLine
                  y={0}
                  stroke="#52525b"
                  strokeWidth={1}
                />
                <Tooltip content={barTooltip} />
                <Bar
                  dataKey="account"
                  fill={primaryColor}
                  name="Account"
                  radius={[2, 2, 0, 0]}
                />
                <Bar
                  dataKey="spy"
                  fill={SPY_COLOR}
                  name="SPY"
                  radius={[2, 2, 0, 0]}
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  formatter={(value: string) => (
                    <span className="text-surface-300 text-xs">{value}</span>
                  )}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Monthly Returns Table */}
      {tableData.length > 0 && (
        <div className="glass-card p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold text-surface-100 mb-3">
            Returns Grid
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="text-xs text-surface-400 bg-surface-800/50">
                  <th className="py-2 px-2 text-left font-medium w-16">Year</th>
                  {MONTH_NAMES.map((m) => (
                    <th key={m} className="py-2 px-1 text-right font-medium">
                      {m}
                    </th>
                  ))}
                  <th className="py-2 px-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-800/50">
                {tableData.map(({ year, monthMap, yearlyReturn }) => (
                  <TableYearRows
                    key={year}
                    year={year}
                    monthMap={monthMap}
                    yearlyReturn={yearlyReturn}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      {data.summary && (
        <div className="glass-card p-3 sm:p-4">
          <h3 className="text-sm sm:text-base font-semibold text-surface-100 mb-3">
            Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SummaryCard
              label="YTD Account"
              value={data.summary.ytd_account_pct}
            />
            <SummaryCard
              label="YTD SPY"
              value={data.summary.ytd_spy_pct}
            />
            <SummaryCard
              label="YTD Alpha"
              value={data.summary.ytd_alpha_pct}
            />
          </div>
          {data.summary.inception_date && (
            <div className="mt-3 pt-3 border-t border-surface-700/50 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryCard
                label="Total Account"
                value={data.summary.total_account_pct}
              />
              <SummaryCard
                label="Total SPY"
                value={data.summary.total_spy_pct}
              />
              <SummaryCard
                label="Total Alpha"
                value={data.summary.total_alpha_pct}
              />
            </div>
          )}
          {data.summary.inception_date && (
            <p className="text-xs text-surface-500 mt-2">
              Since inception: {formatDateFull(data.summary.inception_date)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Sub-components ----

function SummaryCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  const num = parseFloat(value);
  return (
    <div className="metric-card flex items-center justify-between">
      <span className="text-surface-400 text-xs sm:text-sm">{label}</span>
      <span
        className={`text-sm sm:text-base font-semibold ${pctColorClass(num)}`}
      >
        {formatPct(num)}
      </span>
    </div>
  );
}

function TableYearRows({
  year,
  monthMap,
  yearlyReturn,
}: {
  year: number;
  monthMap: Record<number, MonthlyReturn>;
  yearlyReturn?: { account_return_pct: string; spy_return_pct: string };
}) {
  return (
    <>
      {/* Account row */}
      <tr className="hover:bg-surface-800/30 transition-colors">
        <td className="py-2 px-2 text-sm font-medium text-surface-200">
          {year}
        </td>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
          const m = monthMap[month];
          return (
            <td
              key={month}
              className={`py-2 px-1 text-right text-xs sm:text-sm font-medium ${pctColorClass(m?.account_return_pct)}`}
            >
              {m ? formatPct(m.account_return_pct) : "--"}
            </td>
          );
        })}
        <td
          className={`py-2 px-2 text-right text-xs sm:text-sm font-semibold ${pctColorClass(yearlyReturn?.account_return_pct)}`}
        >
          {yearlyReturn ? formatPct(yearlyReturn.account_return_pct) : "--"}
        </td>
      </tr>
      {/* SPY row */}
      <tr className="hover:bg-surface-800/30 transition-colors">
        <td className="py-1.5 px-2 text-xs text-surface-500 italic">SPY</td>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
          const m = monthMap[month];
          return (
            <td
              key={month}
              className={`py-1.5 px-1 text-right text-xs ${pctColorClass(m?.spy_return_pct)} opacity-70`}
            >
              {m ? formatPct(m.spy_return_pct) : "--"}
            </td>
          );
        })}
        <td
          className={`py-1.5 px-2 text-right text-xs font-medium ${pctColorClass(yearlyReturn?.spy_return_pct)} opacity-70`}
        >
          {yearlyReturn ? formatPct(yearlyReturn.spy_return_pct) : "--"}
        </td>
      </tr>
    </>
  );
}
